import { Component, OnInit, OnDestroy, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { WorkOrderDetailsService } from './work-order-details.service';
import { WorkOrderService } from './work-order.service';
import { WorkOrderDto } from '@app/generated/model';
import { NetworkStatusService } from '@app/core/services/network-status.service';
import { LoaderService } from '@app/header/loader/loader.service';
import { ChangeLogService } from '@app/core/services/change-log.service';
import { OfflineDataDb } from '@app/core/db/offline-data';
import { ChangeSyncService } from '@app/core/services/change-sync.service';
import { VersionCheckService } from '@app/core/services/version-check.service';
import { WorkOrderCommentsService } from './work-order-comments.service';
import { env } from 'src/environments/env';
import { firstValueFrom } from 'rxjs';
import { Subject, interval, merge, of } from 'rxjs';
import { takeUntil, filter, switchMap, skip, take } from 'rxjs/operators';
import { ApiLoadErrorService } from '@app/core/services/api-load-error.service';
import { LoggingService } from '@app/shared/services/logging.service';
import { TelemetryScope } from '@app/core/services/app-insights.service';

@Component({
  selector: 'app-work-order',
  templateUrl: './work-order.component.html',
  styleUrls: ['./work-order.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WorkOrderComponent implements OnInit, OnDestroy {
  private readonly offlineRecoveryRetryIntervalMs = env.get().offlineRecoveryRetryIntervalMs ?? 10_000;
  workOrderId!: string;
  loading = true;
  notFound = false;
  showTimer = false;
  workOrder: WorkOrderDto | null = null;
  showSplitView = false;
  private readonly defaultSplitPanelWidth = 420;
  splitPanelWidth = this.defaultSplitPanelWidth;

  private isResizing = false;
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private resizeEventScheduled = false;
  private readonly destroy$ = new Subject<void>();
  isOfflineFallback = false;
  isRetryingOnline = false;
  isOnline = navigator.onLine;
  isBackendReachable = true;
  pendingChangeCount = 0;
  lastDownloadedAt: Date | null = null;
  onlineSyncBannerState: 'hidden' | 'syncing' | 'saved' = 'hidden';
  loadErrorMessage: string | null = null;
  private awaitingOnlineRecovery = false;
  private hasRecoveredOnlineData = false;
  private hadPendingChangesWhileOffline = false;
  private commentsReloadInProgress = false;
  private readonly telemetryScopes: TelemetryScope[] = [];
  private openedTracked = false;

  constructor(
    private route: ActivatedRoute,
    private workOrderService: WorkOrderDetailsService,
    private workOrderSplitService: WorkOrderService,
    private networkStatusService: NetworkStatusService,
    private loaderService: LoaderService,
    private changeLogService: ChangeLogService,
    private changeSyncService: ChangeSyncService,
    private versionCheckService: VersionCheckService,
    private offlineData: OfflineDataDb,
    private apiLoadErrorService: ApiLoadErrorService,
    private log: LoggingService,
  ) {}

  async ngOnInit() {
    this.loading = true;
    this.notFound = false;
    this.loadErrorMessage = null;
    this.apiLoadErrorService.clear();

    this.apiLoadErrorService.changes.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      this.loadErrorMessage = state?.message ?? null;
    });

    this.networkStatusService
      .isOnline()
      .pipe(takeUntil(this.destroy$))
      .subscribe((online) => {
        this.isOnline = online;
        if (!online) {
          this.onlineSyncBannerState = 'hidden';
          this.awaitingOnlineRecovery = false;
          this.hasRecoveredOnlineData = false;
        }
        this.updateOfflineFallback();
      });

    this.loaderService.backendReachableStream.pipe(takeUntil(this.destroy$)).subscribe((reachable) => {
      this.isBackendReachable = reachable;
      if (!reachable) {
        this.onlineSyncBannerState = 'hidden';
        this.awaitingOnlineRecovery = false;
        this.hasRecoveredOnlineData = false;
      }
      this.updateOfflineFallback();
    });

    const workOrderId = this.route.snapshot.paramMap.get('id');
    if (!workOrderId) {
      this.notFound = true;
      this.loading = false;
      return;
    }

    this.workOrderId = workOrderId;
    this.telemetryScopes.push(this.log.pushProperty('WorkOrderId', workOrderId));

    this.workOrderService.initialize(this.workOrderId);
    this.workOrderService.data$.subscribe(
      (data) => {
        this.workOrder = data;
        this.loading = false;
        void this.loadLastDownloadedAt();
        if (data) {
          this.trackOpened(data);
          this.updateOfflineFallback();
          if (
            this.awaitingOnlineRecovery &&
            this.isOnline &&
            this.isBackendReachable &&
            !this.workOrderService.didUseOfflineFallback()
          ) {
            this.awaitingOnlineRecovery = false;
            this.hasRecoveredOnlineData = true;
            if (this.hadPendingChangesWhileOffline) {
              if (this.pendingChangeCount === 0) {
                this.showSavedBanner();
              } else {
                this.onlineSyncBannerState = 'syncing';
              }
            } else {
              this.onlineSyncBannerState = 'hidden';
            }
          }
        }
      },
      (error) => {
        console.error(error);
        this.notFound = true;
      },
    );

    this.workOrderSplitService.showSplitView$.pipe(takeUntil(this.destroy$)).subscribe((show) => {
      this.showSplitView = show;
      if (show) {
        this.splitPanelWidth = this.getDefaultSplitPanelWidth();
      }
    });

    this.changeLogService.discarded$.pipe(takeUntil(this.destroy$)).subscribe(async (refs) => {
      if (!refs.includes(this.workOrderId)) return;
      this.onlineSyncBannerState = 'hidden';
      if (this.isOnline && this.isBackendReachable) {
        await this.performWorkOrderRefresh();
      }
    });

    // Reactively update the pending change count for this work order.
    merge(of(null), this.changeLogService.changes$)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.changeLogService.getPending(this.workOrderId)),
      )
      .subscribe((events) => {
        this.pendingChangeCount = events.length;
        if (this.isOfflineFallback && this.pendingChangeCount > 0) {
          this.hadPendingChangesWhileOffline = true;
        }

        // If syncing just finished and there are no more pending changes, show saved only for offline-created changes.
        if (
          this.pendingChangeCount === 0 &&
          this.onlineSyncBannerState === 'syncing' &&
          this.isOnline &&
          !this.isOfflineFallback &&
          this.hadPendingChangesWhileOffline
        ) {
          this.showSavedBanner();
        }
      });

    this.changeSyncService.syncEvents$.pipe(takeUntil(this.destroy$)).subscribe(async (event) => {
      if (!event.refs.includes(this.workOrderId)) {
        return;
      }

      if (event.state === 'rejected') {
        this.onlineSyncBannerState = 'hidden';
        if (this.isOnline && this.isBackendReachable) {
          await this.performWorkOrderRefresh();
        }
        return;
      }

      if (event.state === 'syncing') {
        if (
          this.hadPendingChangesWhileOffline &&
          (this.awaitingOnlineRecovery || this.onlineSyncBannerState === 'syncing')
        ) {
          this.onlineSyncBannerState = 'syncing';
        }
        return;
      }

      if (event.state === 'success' && this.isOnline) {
        await this.loadLastDownloadedAt();
        const pendingEvents = await this.changeLogService.getPending(this.workOrderId);
        if (pendingEvents.length === 0) {
          this.reconcileCommentsAfterSync();
        }
        if (this.isOfflineFallback || !this.hadPendingChangesWhileOffline) {
          this.onlineSyncBannerState = 'hidden';
        } else if (pendingEvents.length === 0) {
          this.showSavedBanner();
        } else {
          this.onlineSyncBannerState = 'syncing';
        }
        return;
      }

      if (event.state === 'error' && !this.isOfflineFallback) {
        this.onlineSyncBannerState = 'hidden';
      }
    });

    // Retry online recovery with a lightweight version probe on a fixed interval while offline fallback is showing.
    interval(this.offlineRecoveryRetryIntervalMs)
      .pipe(
        takeUntil(this.destroy$),
        filter(() => this.isOfflineFallback && !this.isRetryingOnline),
      )
      .subscribe(() => this.retryOnlineRecovery());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.workOrderService.dispose();
    this.telemetryScopes.forEach((scope) => scope.dispose());
  }

  private trackOpened(workOrder: WorkOrderDto): void {
    if (this.openedTracked) {
      return;
    }
    this.openedTracked = true;
    this.telemetryScopes.push(this.log.pushProperties({ ProjectId: workOrder.projectId, SiteNo: workOrder.siteNo }));
    this.log.event('WorkOrderOpened', { OfflineFallback: this.workOrderService.didUseOfflineFallback() });
  }

  startResize(event: MouseEvent): void {
    this.isResizing = true;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.splitPanelWidth;
    event.preventDefault();
  }

  startResizeTouch(event: TouchEvent): void {
    this.isResizing = true;
    this.resizeStartX = event.touches[0].clientX;
    this.resizeStartWidth = this.splitPanelWidth;
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isResizing) return;
    const delta = this.resizeStartX - event.clientX;
    this.splitPanelWidth = Math.max(200, Math.min(window.innerWidth - 350, this.resizeStartWidth + delta));
    this.notifyLayoutResize();
  }

  @HostListener('document:touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    if (!this.isResizing) return;
    const delta = this.resizeStartX - event.touches[0].clientX;
    this.splitPanelWidth = Math.max(200, Math.min(window.innerWidth - 350, this.resizeStartWidth + delta));
    this.notifyLayoutResize();
    event.preventDefault();
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this.isResizing = false;
    window.dispatchEvent(new Event('resize'));
  }

  @HostListener('document:touchend')
  onTouchEnd(): void {
    this.isResizing = false;
    window.dispatchEvent(new Event('resize'));
  }

  private notifyLayoutResize(): void {
    if (this.resizeEventScheduled) {
      return;
    }
    this.resizeEventScheduled = true;
    requestAnimationFrame(() => {
      this.resizeEventScheduled = false;
      window.dispatchEvent(new Event('resize'));
    });
  }

  private getDefaultSplitPanelWidth(): number {
    const available = window.innerWidth - 55;
    return Math.max(200, Math.min(this.defaultSplitPanelWidth, Math.floor(available / 2)));
  }

  async retryOnlineRecovery() {
    if (this.isRetryingOnline) {
      return;
    }

    this.isRetryingOnline = true;

    try {
      this.apiLoadErrorService.clear();
      const versionProbeSucceeded = await this.versionCheckService.probeVersion();
      if (!versionProbeSucceeded) {
        return;
      }

      if (this.pendingChangeCount > 0 && this.hadPendingChangesWhileOffline) {
        this.onlineSyncBannerState = 'syncing';
      }

      await this.performWorkOrderRefresh();
    } finally {
      this.isRetryingOnline = false;
    }
  }

  private async performWorkOrderRefresh(): Promise<void> {
    this.notFound = false;
    this.apiLoadErrorService.clear();
    this.awaitingOnlineRecovery = this.isOnline && this.isBackendReachable && this.isOfflineFallback;
    try {
      const refreshCompleted = firstValueFrom(this.workOrderService.data$.pipe(skip(1), take(1)));
      await this.workOrderService.refresh();
      await refreshCompleted;
      await this.loadLastDownloadedAt();
      this.updateOfflineFallback();

      const pendingEvents = await this.changeLogService.getPending(this.workOrderId);
      this.pendingChangeCount = pendingEvents.length;
      if (
        !this.isOfflineFallback &&
        this.pendingChangeCount === 0 &&
        this.onlineSyncBannerState === 'syncing' &&
        this.hadPendingChangesWhileOffline
      ) {
        this.showSavedBanner();
      }
    } catch (error) {
      console.warn('[WorkOrder] Retry online failed, keeping offline fallback.', error);
      await this.loadLastDownloadedAt();
      this.updateOfflineFallback();
    }
  }

  private updateOfflineFallback() {
    // Offline is determined by probe-confirmed reachability (backendReachableStream is
    // only flipped to false after a failed check-update probe) plus the browser online
    // flag. A single failed request no longer forces the page offline; didUseOfflineFallback()
    // remains a data-provenance signal used by the online-recovery logic below.
    this.isOfflineFallback = !this.isOnline || !this.isBackendReachable;
  }

  private async loadLastDownloadedAt(): Promise<void> {
    if (!this.workOrderId) {
      this.lastDownloadedAt = null;
      return;
    }

    const offlineWorkOrder = await this.offlineData.workOrders.get(this.workOrderId);
    this.lastDownloadedAt = offlineWorkOrder?.lastUpdated ? new Date(offlineWorkOrder.lastUpdated) : null;
  }

  @HostListener('window:offline')
  onBrowserOffline() {
    this.isOnline = false;
    this.updateOfflineFallback();
  }

  @HostListener('window:online')
  onBrowserOnline() {
    this.isOnline = true;
    this.updateOfflineFallback();
  }

  hasStatusBanner(): boolean {
    return this.isOfflineFallback || this.onlineSyncBannerState !== 'hidden';
  }

  dismissSavedBanner(): void {
    this.onlineSyncBannerState = 'hidden';
  }

  private async showSavedBanner(): Promise<void> {
    if ((await this.changeLogService.getRejectedCount(this.workOrderId)) > 0) {
      this.onlineSyncBannerState = 'hidden';
      return;
    }
    this.onlineSyncBannerState = 'saved';
    this.hadPendingChangesWhileOffline = false;
  }

  private reconcileCommentsAfterSync(): void {
    if (this.commentsReloadInProgress) {
      return;
    }

    const commentsService = this.workOrderService.getDataService('COMMENTS') as WorkOrderCommentsService | null;
    if (!commentsService) {
      return;
    }

    this.commentsReloadInProgress = true;
    commentsService.load();
    setTimeout(() => {
      this.commentsReloadInProgress = false;
    }, 250);
  }

  getStatusBannerClass(): string {
    return this.onlineSyncBannerState === 'saved' ? 'status-saved' : 'status-offline';
  }

  getStatusBannerIcon(): string {
    if (this.onlineSyncBannerState === 'saved') {
      return 'wifi';
    }

    if (this.onlineSyncBannerState === 'syncing') {
      return 'sync';
    }

    return 'wifi_off';
  }

  async retryLoadWorkOrder() {
    if (this.isRetryingOnline) {
      return;
    }

    this.isRetryingOnline = true;
    try {
      await this.performWorkOrderRefresh();
    } finally {
      this.isRetryingOnline = false;
    }
  }
}
